<template>
  <div class="Main-Inner">

    <Sheet
      title="New Translations"
      :width="700"
      :visible="adding"
      @close="adding = false"
      padded
    >
      <Layout>
        <LayoutSection>
          <Card>
            <CardSection>
              <ValidationObserver v-slot="{ invalid }" slim>
                <FormLayout>
                  <div>Application: '{{currentApp || 'Base'}}'</div>

                  <validation-provider name="Key" rules="required" v-slot="{ errors }">
                    <TextField
                      label="Key"
                      v-model="newTrans.key"
                      ref="newKey"
                      :error="errors[0]"
                    />
                  </validation-provider>

                  <validation-provider name="Base Value" rules="required" v-slot="{ errors }">
                    <TextField
                      label="Base Value (English)"
                      v-model="newTrans.value"
                      :error="errors[0]"
                      multiline
                    />
                  </validation-provider>
                  
                  <Button type="primary" :disabled="saving || invalid" @click="newKeySave">Add Key</Button>

                </FormLayout>
              </ValidationObserver>
            </CardSection>
          </Card>

        </LayoutSection>
      </Layout>
    </Sheet>

    <Sheet
      title="Editing"
      :width="700"
      :visible="!!currentTrans"
      @close="cancel"
      padded
    >
      <!-- {{ currentTrans.id }} -->
      <!-- {{ currentTrans.lang }} -->
      <Layout v-if="currentTrans">
        <LayoutSection>
          <Card>
            <CardSection>
              <FormLayout>

                <div>Application: '{{currentApp || 'Base'}}'</div>

                <div>Key: '<span class="key">{{ currentTrans.key }}</span>'</div>

                <div>English: {{ currentTrans.baseValue }}</div>

                <hr />

                <TextField
                  :label="`Translation in ${$t(`language.${currentLanguage}`)}`"
                  v-model="currentTrans.value"
                  multiline
                />

                <Checkbox
                  label="Approved?"
                  v-model="currentTrans.approved"
                  :trueValue="1"
                  :falseValue="0"
                  help-text="Approve translations when they are ready for the site."
                />

                <Checkbox 
                  v-if="showAdminFeatures && currentLanguage === 'en'" 
                  v-model="regenerateTranslations"
                  label="Automatically regenerate all non-English translations to reflect this change."
                />

                <Stack>
                  <Button type="primary" :disabled="saving" @click="save(currentTrans)">Save</Button>
                  <Button v-if="onBase" :disabled="saving" @click="preview">Preview</Button>
                </Stack>
                <template v-if="showAdminFeatures">
                  <hr />
                  <Stack>
                    <Button type="destructive" :disabled="saving" @click="removeKey(currentTrans)">Remove</Button>
                    <Button :disabled="saving" @click="renameKey(currentTrans)">Rename</Button>
                  </Stack>
                </template>
              </FormLayout>
            </CardSection>
          </Card>

        </LayoutSection>
      </Layout>
    </Sheet>

    <Sheet
      title="Editing - JSON Mode"
      :width="800"
      :visible="!!editingJson"
      @close="cancelJson"
      padded
    >
      <Layout id="TranslationJson" v-if="editingJson">
        <LayoutSection>
          <Card>
            <CardSection>
              <FormLayout>

                <div>Application: '{{currentApp || 'Base'}}'</div>

                <hr />

                <TextField
                  class="input-json-text"
                  label="JSON"
                  v-model.trim="json"
                  multiline
                />

                <Stack>
                  <Button type="primary" :disabled="saving" @click="saveBulk">Save</Button>
                </Stack>
              </FormLayout>
            </CardSection>
          </Card>

        </LayoutSection>
      </Layout>
    </Sheet>

    <div id="TranslationLayout" class="Main-Content" :class="{ 'sList': style === 'list', 'sSide': style === 'side' }">
      <div class="bar">
        <Stack>
          <ButtonGroup segmented>
            <Button @click="changeStyle('list')" :disabled="style === 'list'">Key List</Button>
            <Button @click="changeStyle('side')" :disabled="style === 'side'">Side by Side</Button>
          </ButtonGroup>
          <template v-if="showAdminFeatures" class="bar">
            <Button @click="newKey" type="primary">New Key</Button>
            <Button v-if="currentLanguage === 'en'" @click="editingJson = true" type="primary">JSON Edit</Button>
            <!-- <Button @click="setNewLanguage = true" type="primary">Create New Language</Button> -->
            <Button @click="download">Download "{{ currentApp ? `${currentApp}/${currentLanguage}.json` : `base/${currentLanguage}.json`}}"</Button>
            <Button @click="downloadAll">Download All</Button>
          </template>
        </Stack>
      </div>
      <div class="content">
        <div class="left">
          <div class="filters">
            <Layout>
              <LayoutSection>
                <Stack>

                  <Select v-model="currentLanguage"
                    label="Editing Language"
                    :options="supportedLanguages"                  
                  />

                  <Select
                    v-model="currentApp"
                    label="Editing Application"
                    :options="apps"
                  />
                  <TextField
                    label="Key"
                    v-model="filterKey"
                  />
                  <TextField
                    label="Value"
                    v-model="filterValue"
                  />

                  <Checkbox
                    label="Exact Match?"
                    v-model="filterExact"
                  />

                  <Select
                    label="Downloaded"
                    v-model="filterDownloaded"
                    :options="[
                      { label: 'All', value: '' },
                      { label: 'Yes', value: 1 },
                      { label: 'No', value: 0 },
                    ]"
                  />
                  <Select
                    label="Approved"
                    v-model="filterApproved"
                    :options="[
                      { label: 'All', value: '' },
                      { label: 'Yes', value: 1 },
                      { label: 'No', value: 0 },
                    ]"
                  />
                </Stack>

                <div class="stats">
                  <span>Approved: {{ numApproved }} / {{ translations.length }}</span>
                  <span>Downloaded: {{ numDownloaded }} / {{ translations.length }}</span>
                </div>
              </LayoutSection>
            </Layout>
          </div>
          <div class="keys">
            <template v-if="style === 'list'">
              <div v-for="t of translationsFiltered" :key="t.id" @click="select(t)" :class="{ selected: currentTrans && currentTrans.id === t.id }">{{ t.key }}</div>
            </template>
            <template v-if="style === 'side'">
              <template v-if="loading">
                <LoadingLayout />
              </template>
              <template v-else>
                <table class="list-table" cellspacing="0">
                  <thead>
                    <td>English</td>
                    <td>{{ $t(`language.${currentLanguage}`) }}</td>
                    <td></td>
                  </thead>
                  <tbody>
                    <tr v-for="t of translationsFilteredTruncated" :key="t.id">
                      <td class="truncate"><span>{{ t.baseValue }}</span></td>
                      <td class="truncate"><span>{{ t.value }}</span></td>
                      <td>
                        <Stack>
                          <Button :disabled="saving" @click="select(t)">Edit</Button>
                          <Button :disabled="saving || t.approved" @click="approve(t)">Approve</Button>
                        </Stack>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <Button v-if="sideBySideShown <= translationsFiltered.length" type="primary" class="more-button" @click="sideBySideShown += 20">More ⬇</Button>
              </template>
            </template>
          </div>
        </div>
      </div>
    </div>

    <Modal
      title="New Language"
      :visible="!!setNewLanguage"
      @close="setNewLanguage = false"
    >
      <Layout v-if="setNewLanguage">
        <LayoutSection>
          <FormLayout>
            <TextField
              label="New Language Iso"
              v-model.trim="newLanguage"
              help-text="Clicking 'Create' will start the process of copying all keys into the new language. This may take some time."
            />
            <Stack>
              <Button type="primary" :disabled="saving" @click="createNewLanguage">Create</Button>
            </Stack>
          </FormLayout>
        </LayoutSection>
      </Layout>
    </Modal>
           

  </div>
</template>

<script>

import Downloader from '../classes/utils/Downloader'
import Env from '../classes/utils/Env'

export default {
  name: 'Translations',
  data() {
    return {
      loading: true,
      saving: false,
      adding: false,
      shortkeyListener: null,
      translations: [],
      currentLanguage: '',
      currentApp: '',
      regenerateTranslations: false,

      currentTrans: null,
      style: 'side',
      sideBySideShown: 20,

      filterKey: '',
      filterValue: '',
      filterExact: false,
      filterDownloaded: '',
      filterApproved: '',

      translationChannel: null,
      newTrans: {
        key: '',
        value: ''
      },

      json: '',
      editingJson: false,
      newLanguage: '',
      setNewLanguage: false,

      showAdminFeatures: false
    }
  },
  created() {
    // don't even show the page if it's not super
    if (!this.theUser.isSuper) {
      this.$router.replace('/campaigns/')
    }
    window.document.getElementById('LanguageChooserButton').style.display = 'none';
    this.addShortkeyListener()

    if ('BroadcastChannel' in self) {
      this.translationChannel = new BroadcastChannel('translation')
    }
  },
  beforeDestroy() {
    window.document.getElementById('LanguageChooserButton').style.display = 'flex';
    window.removeEventListener('keydown', this.shortkeyListener)
  },
  async mounted() {
    this.currentLanguage = this.theUser.defaultLanguage

    this.$watch('currentLanguage', () => {
      this.currentTrans = null
      this.translations = []
      this.loadTranslations()
    })

    this.$watch('currentApp', () => {
      this.currentTrans = null
      this.translations = []
      this.loadTranslations()
    })

    const n = this.theUser.firstName
    // only work dev and stage, otherwise it's not all languages!
    if (!Env.isProduction() && (n === 'Tarwin' || n === 'Bryce' || n === 'Matt' || n === 'Rajiv')) {
      this.showAdminFeatures = true
    }

    this.loadTranslations()
  },
  methods: {
    loadTranslations() {
      NProgress.start()
      this.loading = true
      Promise.all([this.api.getBaseTranslations(this.currentApp), this.api.getTranslations(this.currentLanguage, this.currentApp)])
        .then((response) => {

          this.translations = response[1]
          
          // go over base
          const baseLookup = {}
          for (const t of response[0]) {
            baseLookup[t.key] = t
          }

          // add base to translation
          for (const t of this.translations) {
            t.baseValue = baseLookup[t.key].value
          }

          this.loading = false

          const dict = {}
          for (const t of this.translations) {
            dict[t.key] = t.value
          }
          this.broadcastLanguage(dict)
        })
        .catch(this.apiCatch) 
        .finally(()=> {
          NProgress.done()
        })
    },
    addShortkeyListener() {
      this.shortkeyListener = window.addEventListener('keydown', (e) => {
        const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
        const isMod = isMac ? e.getModifierState('Meta') : e.getModifierState('Control')

        if (e.key === 'm' && isMod) {
          e.preventDefault()
          e.stopPropagation()
          this.newKey()
        }
      })
    },
    select(trans) {
      if (!this.saving) {
        this.currentTrans = Object.assign({}, trans)
      }
    },
    cancel() {
      this.currentTrans = null
      this.regenerateTranslations = false
      const dict = {}
      for (const t of this.translations) {
        dict[t.key] = t.value
      }
      this.broadcastLanguage(dict)
    },
    saveBulk() {
      let obj = {}
      try {
        obj = JSON.parse(this.json)
      } catch (e) {
        this.$ui.alert({
          title: 'Error',
          content: 'Invalid JSON',
          primaryActionLabel: 'Okay'
        })
        return
      }
      this.saving = true
      NProgress.start()
      this.api.saveBulkJsonTranslation(
        this.json, 
        this.currentApp, 
        this.supportedLanguages.map(l => l.value).filter(l => l !== 'en')
      )
      .then((newTrans) => {
        for (const k of Object.keys(obj)) {
          let actual = this.translations.find(t => t.key === k)
          if (!actual) {
            // add
            const foundNew = newTrans.find(t => t.key === k)
            if (foundNew) this.translations.push(foundNew)
          } else if (obj[k]) {
            // save
            actual.value = obj[k]
            actual.approved = 0
            actual.downloaded = 0
            actual.baseValue = obj[k]
          } else {
            // remove
            this.translations.splice(this.translations.findIndex(t => t.key === k), 1)
          }
        }
        this.json = ''
        this.editingJson = false

      })
      .catch(this.apiCatch)
      .finally(() => {
        this.saving = false
        NProgress.done()
      })
    },
    async approve(trans) {
      trans.approved = true
      await this.save(trans, false)
    },
    async save(trans, broadcast = true) {
      this.saving = true

      NProgress.start()
      let apiCall = []
      if (this.regenerateTranslations) {
        apiCall = [this.api.saveTranslationAndRegenerate(
          trans.id, 
          trans.value, 
          this.currentApp, 
          this.supportedLanguages.map(l => l.value).filter(l => l !== 'en')
        )]
      } else {
        apiCall = [this.api.saveTranslation(
          trans.id,
          trans.value,
          trans.approved ? 1 : 0, 
          this.currentApp
        )]
      }
      Promise.all(apiCall)
      .then(() => {
        let actual = this.translations.find(t => t.id === trans.id)
        actual.value = trans.value
        actual.approved = trans.approved ? 1 : 0
        actual.downloaded = 0
        if (this.currentLanguage === 'en') {
          actual.baseValue = trans.value
        }

        this.currentTrans = null
        this.regenerateTranslations = false

        if (broadcast) {
          const dict = {}
          for (const t of this.translations) {
            dict[t.key] = t.value
          }
          this.broadcastLanguage(dict)
        }
      })
      .catch(this.apiCatch) 
      .finally(()=> {
        NProgress.done()
        this.saving = false
      })
    },
    preview() {
      const dict = {}
      for (const t of this.translations) {
        dict[t.key] = t.value
      }
      dict[this.currentTrans.key] = this.currentTrans.value
      this.broadcastLanguage(dict)
    },
    broadcastLanguage(dict) {
      if (this.translationChannel) {
        this.translationChannel.postMessage({
          // locale: this.currentLanguage,
          locale: 'TEST',
          dictionary: dict
        })
      } else {
        console.warn('BroadcastChannel not supported')
      }
    },
    changeStyle(newStyle) {
      this.style = newStyle
    },
    newKey() {
      this.newTrans = {
        key: '',
        value: ''
      }
      this.adding = true
    },
    cancelJson() {
      this.editingJson = false
      this.json = ''
    },
    createNewLanguage() {
      if (this.supportedLanguages.map(l => l.value).find(l => l === this.newLanguage)) {
        this.$ui.alert({
          title: 'Error',
          content: 'Language already exists',
          primaryActionLabel: 'Okay'
        })
        return
      }
      this.saving = true
      NProgress.start()
      this.api.createNewLanguage(this.newLanguage)
        .then(() => {
          this.newLanguage = ''
          this.setNewLanguage = false
        })
        .catch(this.apiCatch)
        .finally(() => {
          NProgress.done()
          this.saving = false
        })
    },  
    newKeySave() {
      const key = this.newTrans.key.trim()
      for (const trans of this.translations) {
        if (trans.key.toLowerCase() === key.toLowerCase()) {
          this.$ui.alert({
            title: `Key "${trans.key}" already exists.`
          })
          return
        }
      }
      this.saving = true
      NProgress.start()
      this.api.createTranslation(key, this.newTrans.value.trim(), this.supportedLanguages.map(l => l.value).filter(l => l !== 'en'), this.currentApp)
      .then((response) => {

        // incldues EN now
        const translations = response.map(o => {
          return {
            ...o,
            approved: 0,
            downloaded: 0
          }
        })

        for (const trans of translations) {
          if (trans.lang === this.currentLanguage) {
            this.translations.push(trans)
            break
          }
        }

        this.adding = false
      })
      .catch(this.apiCatch) 
      .finally(()=> {
        this.saving = false
        NProgress.done()
      })
    },
    async downloadAll() {
      NProgress.start()
      const self = this
      const allTrans = {}
      const translationApiCalls = []
      this.supportedLanguages.forEach((lang) => {
        translationApiCalls.push(this.api.getTranslations(lang.value, this.currentApp))
      })
      translationApiCalls.push(this.api.downloadAllTranslations(this.currentApp))

      Promise.all(translationApiCalls)
        .then((response) => {
          this.supportedLanguages.forEach((lang, index) => {
            let translations = response[index]
            const out = {}
            if (lang.value === self.currentLanguage) {
              translations = self.translations
            }
            translations.sort((a, b) => {
              if (a.key > b.key) return 1
              if (a.key < b.key) return -1
              return 0
            })

            for (const trans of translations) {
              out[trans.key] = trans.value
              if (lang.value === self.currentLanguage) {
                trans.downloaded = 1
              }
            }
            allTrans[lang.value] = out
          })
          return Downloader.downloadZip(this.currentApp, this.supportedLanguages, allTrans)
        })
        .catch(this.apiCatch)
        .finally(() => {
          NProgress.done()
        })
    },
    download() {
      // this just sets downloaded flag
      this.api.downloadTranslations(this.currentLanguage, this.currentApp)
      .then(() => {
        // now do local download
        const out = {}
        const translations = this.translations
        translations.sort((a, b) => {
          if (a.key > b.key) return 1
          if (a.key < b.key) return -1
          return 0
        })
        // and set all locally as downloaded
        for (const trans of translations) {
          out[trans.key] = trans.value
          trans.downloaded = 1
        }
        const t = {}
        t[this.currentLanguage] = out
        Downloader.downloadZip(this.currentApp, this.supportedLanguages, t)
      })
      .catch(self.apiCatch) 
      .finally(()=> {
        self.saving = false
        NProgress.done()
      })
    },
    removeKey(trans) {
      const self = this
      this.$ui.confirm({
        title: `Remove translation "${trans.key}"?`,
        onConfirm() {
          this.saving = true
          NProgress.start()
          self.api.removeTranslation(trans.key, self.currentApp)
          .then(() => {
            // remove
            self.translations.splice(self.translations.findIndex(t => t.key === trans.key), 1)
            self.currentTrans = null
          })
          .catch(self.apiCatch) 
          .finally(()=> {
            self.saving = false
            NProgress.done()
          })
        }
      })
    },
    renameKey(trans) {
      const self = this
      this.$ui.prompt({
        title: `Rename translation "${trans.key}"?`,
        inputValue: trans.key,
        inputPlaceholder: 'Key',
        onAction(newKey) {
          this.saving = true
          NProgress.start()
          self.api.renameTranslationKey(trans.key, newKey, self.currentApp)
          .then(() => {
            // rename key
            self.translations.find(t => t.key === trans.key).key = newKey
            self.translations.find(t => t.key === trans.key).downloaded = 0
            self.currentTrans.key = newKey
          })
          .catch(self.apiCatch) 
          .finally(()=> {
            self.saving = false
            NProgress.done()
          })
        }
      })
    }
  },
  computed: {
    translationsFiltered() {
      let data = this.translations

      if (this.filterKey.trim()) {
        const k = this.filterKey.toLowerCase().trim()
        if (this.filterExact) {
          data = data.filter(d => d.key.toLowerCase() === k)
        } else {
          data = data.filter(d => d.key.toLowerCase().indexOf(k) > -1)
        }
      }

      if (this.filterDownloaded !== '') {
        data = data.filter(d => d.downloaded === this.filterDownloaded)
      }

      if (this.filterApproved !== '') {
        data = data.filter(d => d.approved === this.filterApproved)
      }

      if (this.filterValue.trim()) {
        const k = this.filterValue.toLowerCase().trim()
        if (this.filterExact) {
          data = data.filter(d => {
            return d.value.toLowerCase() === k
            || d.baseValue.toLowerCase() === k
          })
        } else {
          data = data.filter(d => {
            return d.value.toLowerCase().indexOf(k) > -1
            || d.baseValue.toLowerCase().indexOf(k) > -1
          })
        }
      }

      return data
    },
    translationsFilteredTruncated() {
      return this.translationsFiltered.slice(0, this.sideBySideShown)
    },
    numApproved() {
      return this.translations.filter(t => t.approved).length
    },
    numDownloaded() {
      return this.translations.filter(t => t.downloaded).length
    },
    onBase() {
      return !this.currentTrans
    },
    apps() {
      return [
        {
          label: 'Base',
          value: ''  
        },
        {
          label: 'Insights',
          value: 'insights'
        },
        {
          label: 'Ruleset Editor',
          value: 'rulesetEditor'
        }
      ]
    }
  },
  components: {
  }
}
</script>

<style lang="scss">
#TranslationJson {
  .input-json-text {
    textarea {
      min-height: 300px;
      max-height: 500px;
    }
  }
}
#TranslationLayout.Main-Content {
  display: flex;
  flex-direction: column;
  max-width: 100% !important;
  background: #fff !important;
  .bar {
    flex: 0 0 50px;
    border-bottom: 1px solid #eee;
    padding: 15px;
  }
  .content {
    flex: 1;
    display: flex;
    flex-direction: row;
    .left {
      display: flex;
      flex-direction: column;
      flex: 100%;
      border-right: 1px solid #eee;
      .filters {
        flex: 0 0 50px;
        border-bottom: 1px solid #eee;
        padding: 15px;
        .stats {
          margin-top: 15px;
          span {
            display: inline-block;
            padding-right: 15px;
          }
        }
      }
      .keys {
        padding: 15px;
        overflow-y: auto;
        overflow-x: hidden;
        // width: 700px;
        height: calc(100vh - 310px) !important;
        & > div {
          cursor: pointer;
          white-space: nowrap;
          padding: 5px;
          border-radius: 3px;
          border: 1px solid #fff;
          &:hover {
            border: 1px solid #ccc;
          }
          &.selected {
            background: #eee;
          }
        }
      }
    }
    .right {
      flex: 100%;
      padding: 25px;
      .key {
        font-family: monospace;
      }
    }
  }


  // ----------
  &.sSide {
    .content {
      .left {
        flex: 100%;
        .keys {
          width: 100%;
          .list-table {
            width: 100%;
            tr {
              padding: 0;
              margin: 0;
              &:hover {
                background: #f6f6f6;
              }
            }
            thead {
              td {
                padding: 25px;
                font-weight: bold;
              }
            }
            tbody {
              td {
                border-bottom: 1px solid #ccc;
              }
              .truncate {
                padding: 15px;
                span {
                  width: 40vw;
                  display: block;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                }
              }
            }
          }
          .more-button {
            margin-top: 15px;
            margin-bottom: 25px;
            float: right;
          }
        }
      }
      // .right {
      //   display: none;
      // }
    }
  }
}
</style>